import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom'
import {
  ChevronLeft, ArrowLeft, Save, Trash2, AlertCircle, LayoutTemplate, PanelRightClose,
  PanelRightOpen, Eye, PenLine, Pencil, Globe, Tags, Monitor, Tablet, Smartphone,
} from 'lucide-react'
import jsyaml from 'js-yaml'
import { useBuilderObjectType } from './hooks/useBuilderObjectTypes'
import { useBuilderObject, useBuilderObjects } from './hooks/useBuilderObjects'
import { useBuilderModelsFor } from './hooks/useBuilderPages'
import { useOrbitStickyTop } from './hooks/useOrbitStickyTop'
import { useBuilder } from './hooks/useBuilder'
import { BuilderProvider, DEVICE_WIDTHS } from './BuilderContext'
import { BuilderCanvas } from './BuilderCanvas'
import { BuilderDryRunPanel } from './BuilderDryRunPanel'
import { BuilderMetaPanel } from './BuilderMetaPanel'
import { BuilderObjectAddressPanel } from './BuilderObjectAddressPanel'
import { BuilderObjectFieldsPanel } from './BuilderObjectFieldsPanel'
import { FieldEditorPopover } from './editors/FieldEditorPopover'
import { FieldRow } from './editors/FieldRow'
import { hasFieldValue } from './editors/fieldValue'
import { applyContextMapping, previewContextValues, resolveContextField } from './editable/contextFields'
import { previewModelPath } from './modelPath'
// Amorce le registre des éditeurs (text, image-drupal, lien, liste…) : l'écran
// peut être atteint sans être passé par le shell du builder.
import './editors'

/**
 * Page d'édition d'une instance d'objet (`builder_unit`).
 *
 * Routes :
 *   /builder/types/:objectType/new            — création (id absent)
 *   /builder/types/:objectType/instance/:id   — édition d'une instance
 *
 * UNE SEULE SURFACE : le contenu s'édite LÀ OÙ IL S'AFFICHE. Le MODÈLE de son
 * type — une page qui déclare le servir (`modelFor`) — est monté en LUNETTE
 * au-dessus de l'objet ; le modèle n'y est pas modifiable (`editScope:
 * 'object'`, cf. BuilderContext) et seuls ses éléments liés à un champ réel du
 * type le sont — c'est `resolveContextField` qui tranche. Ce qui part au save
 * reste l'OBJET : ni la page, ni ses blocs, ni les unités partagées qu'elle
 * monte.
 *
 * À CÔTÉ, et toujours : le PANNEAU DES CHAMPS — les champs du `fields[]` du def
 * rendus par le registre d'éditeurs (`getEditor(field.type)`), inline pour ceux
 * qui s'éditent en place, pop-in ANCRÉE au champ pour les autres. Il n'est pas
 * un mode concurrent mais le complément de la lunette : c'est par lui qu'on
 * atteint les champs que le modèle n'affiche pas. Il a son propre défilement et
 * se redimensionne (cf. BuilderObjectFieldsPanel). Un type SANS modèle n'a rien
 * à mettre dans la lunette : le panneau tient alors l'écran, seul.
 *
 * Il n'y a plus de « mode formulaire » à choisir, ni de bascule vers lui : la
 * question « formulaire ou modèle ? » ne se posait qu'à l'éditeur, jamais au
 * contenu. `?model=<slug>` reste dans l'URL pour DÉSIGNER le modèle ouvert quand
 * un type hérité en porte plusieurs (la règle est 1 type = 1 modèle) — il se
 * partage et se met en favori.
 *
 * La barre est celle d'une page du builder, aux mêmes gestes : retour, titre
 * saisi en place, Adresse, Métadonnées, Publié, prévisualisation, appareil,
 * enregistrement, corbeille. Ils portent ici sur le CONTENU — son slug (le
 * chemin, lui, appartient au modèle et vaut pour tout le type), ses balises
 * `<head>` (qui l'emportent sur celles du modèle), son état de publication.
 */
export const BuilderObjectEditor = ({ embedded = false }) => {
  // Sous le menu orbit (`embedded`, routes `withMenu`) : remplir le `main`
  // d'OrbitChrome plutôt qu'imposer 100vh, sinon la page dépasse d'une hauteur
  // de barre de menu. Standalone : plein écran comme avant.
  const shellClass = `${embedded ? 'flex-1' : 'min-h-screen'} bg-gray-50 flex flex-col`

  const { objectType: machineName, id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isNew = !id

  const { type, loading: typeLoading, error: typeError } = useBuilderObjectType(machineName)
  const { object, loading: objectLoading, error: objectError, saveObject } = useBuilderObject(machineName, id)
  const { deleteObject } = useBuilderObjects(machineName)
  const { models, loading: modelsLoading } = useBuilderModelsFor(machineName)

  const parsedDef = useMemo(() => {
    if (!type?.definition) return null
    try {
      return jsyaml.load(type.definition) || {}
    } catch {
      return null
    }
  }, [type?.definition])

  // Le SITE du type voyage AVEC chaque champ. Un champ borné au site — une
  // catégorie `term-ref`, qui ne propose que les taxonomies de CE site et y
  // crée les nouvelles — n'a aucun autre moyen de le connaître : ni `FieldRow`
  // ni `resolveContextField` ne lui passent autre chose que le champ.
  // L'injecter ici couvre les deux surfaces, le panneau des champs et l'édition
  // dans la lunette, sans élargir leur contrat. Une valeur déjà écrite dans le
  // schéma gagne : le YAML reste le dernier mot.
  const fields = useMemo(
    () => (parsedDef?.fields || []).map((f) => (
      f && typeof f === 'object' && f.siteId === undefined
        ? { ...f, siteId: type?.siteId ?? null }
        : f
    )),
    [parsedDef, type?.siteId],
  )
  const primaryField = parsedDef?.primaryField || null

  const [data, setData] = useState({})
  const [slug, setSlug] = useState('')
  // La vie publique du contenu — réglée dans la barre, envoyée au save.
  const [meta, setMeta] = useState({})
  const [published, setPublished] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [modal, setModal] = useState(null) // {key} of the field whose editor is open

  // Initial population : defaultData for new instances, loaded data otherwise.
  useEffect(() => {
    if (isNew && parsedDef) {
      setData(parsedDef.defaultData || {})
      setSlug('')
      setMeta({})
      setPublished(true)
    } else if (object) {
      setData(object.data || {})
      setSlug(object.slug || '')
      setMeta(object.meta || {})
      setPublished(object.published !== false)
    }
  }, [isNew, parsedDef, object])

  const setField = (key, value) => {
    setData(prev => ({ ...prev, [key]: value }))
  }

  // ─── Pré-remplissage d'un contenu NEUF ────────────────────────────────────
  // Un modèle n'a pas de contenu propre : ouvert sur un contenu vide, il rend
  // une page vide — l'éditeur ne voit plus ce qu'il édite. Le contenu neuf part
  // donc avec les valeurs d'APERÇU que le modèle affiche déjà (le `_context_`
  // des définitions de ses blocs, cf. `previewContextValues`), qu'il n'a plus
  // qu'à réécrire.
  //
  // UNE SEULE FOIS, et jamais par-dessus une saisie : les définitions arrivent
  // après le premier rendu (le provider les charge), et l'effet qui les guette
  // se rejoue à chaque changement du registre.
  const prefilledRef = useRef(false)
  const applyPreviewPrefill = useCallback((values) => {
    if (prefilledRef.current || !values || Object.keys(values).length === 0) return
    prefilledRef.current = true
    setData((prev) => {
      const next = { ...prev }
      for (const [key, value] of Object.entries(values)) {
        // `hasFieldValue` et non un test de vérité : `0` et `false` sont des
        // valeurs saisies, une chaîne d'espaces n'en est pas une.
        if (!hasFieldValue(next[key])) next[key] = value
      }
      return next
    })
  }, [])
  // Un contenu enregistré n'est plus neuf : le verrou se rearme si l'écran
  // repart en création (nouveau contenu depuis le même écran).
  useEffect(() => { prefilledRef.current = false }, [machineName, isNew])

  // ─── Le modèle ouvert ─────────────────────────────────────────────────────
  // Un contenu s'édite dans le modèle de son type ; il n'y a pas d'autre
  // surface à choisir. `?model=<slug>` (ou `defaultModel:` du def) DÉSIGNE
  // lequel quand un type hérité en porte plusieurs — sinon c'est le premier, et
  // c'est le seul. Un slug qui ne désigne rien se dit, et l'écran ouvre le
  // modèle du type plutôt que de rester vide.
  const modelParam = searchParams.get('model')
  const requestedSlug = (modelParam ?? parsedDef?.defaultModel ?? '').trim()
  const activeModel = requestedSlug
    ? (models.find(m => m.slug === requestedSlug) ?? models[0] ?? null)
    : (models[0] ?? null)
  const modelMissing = requestedSlug !== ''
    && !modelsLoading
    && !models.some(m => m.slug === requestedSlug)

  const selectModel = (nextSlug) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('model', nextSlug)
      return next
    }, { replace: true })
  }

  // Ce que le canevas édite : l'objet, jamais la page. `resolve` est la règle
  // d'éditabilité (une liaison `~~._context_.X` qui tombe sur un champ réel du
  // type), `getValue`/`setValue` la lecture/écriture de CE champ-là.
  //
  // La valeur est lue dans une référence toujours à jour plutôt que dans l'état
  // capturé : deux commits successifs (une pop-in qui écrit l'adresse ET
  // l'intitulé d'un lien) liraient sinon tous les deux la donnée d'avant.
  const dataRef = useRef(data)
  dataRef.current = data
  const objectEdit = useMemo(() => ({
    fields,
    mapping: activeModel?.mapping ?? null,
    resolve: (path) => resolveContextField(path, { fields, mapping: activeModel?.mapping ?? null }),
    getValue: (key) => dataRef.current?.[key],
    setValue: (key, value) => setData(prev => ({ ...prev, [key]: withLocalRender(fields, key, value) })),
  }), [fields, activeModel])

  // Le contexte injecté dans les blocs du modèle — exactement celui que le
  // rendu public fabrique pour un visiteur (même helper), pour que l'éditeur
  // voie ce que le visiteur verra. La donnée tenue ici est celle du display
  // decoupled, donc déjà ENRICHIE (`{mid, alt, url}` pour une image) : le
  // serveur restrippe les clés dérivées au presave, l'aller-retour est
  // symétrique et l'aperçu est juste dès le commit.
  const objectContext = useMemo(
    () => applyContextMapping(data, activeModel?.mapping),
    [data, activeModel],
  )

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const result = await saveObject({
        nid: object?.nid,
        slug: slug || undefined,
        data,
        meta,
        published,
        // Un contenu NEUF naît sur le site de son type (le back le prendrait
        // par défaut ; on le dit). Sans effet sur une mise à jour.
        siteId: isNew ? (type?.siteId ?? null) : null,
      })
      if (isNew) {
        // After create, redirect to the canonical URL so the editor sticks
        // around for further edits with proper nid + slug — en gardant le
        // modèle ouvert (`location.search`).
        navigate(
          `/builder/types/${machineName}/instance/${result.slug || result.nid}${location.search}`,
          { replace: true },
        )
      }
    } catch (e) {
      setSaveError(e.message || 'Erreur de sauvegarde.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!object?.nid) return
    if (!window.confirm(`Supprimer ce contenu ?`)) return
    try {
      await deleteObject(object.nid)
      navigate(`/builder/types/${machineName}`)
    } catch (e) {
      alert(e.message || 'Suppression impossible.')
    }
  }

  // ─── Chargements et impasses ──────────────────────────────────────────────
  // JAMAIS le bandeau « Page Builder » (onglets Pages / Contenus / Types) : il
  // n'a rien à faire sur un écran d'édition, et l'afficher le temps d'un
  // chargement donnait à l'arrivée l'allure d'une redirection vers un autre
  // écran — une barre d'administration apparue puis remplacée. Une bande nue
  // tient la place, à la même hauteur.
  if (typeLoading || (objectLoading && !isNew)) {
    return (
      <div className={shellClass}>
        <PlainBar />
        <div className="flex items-center justify-center py-24" role="status">
          <span className="spinner" aria-hidden="true" />
          <span className="sr-only">Chargement du contenu…</span>
        </div>
      </div>
    )
  }

  if (typeError || !type || !parsedDef) {
    return (
      <div className={shellClass}>
        <PlainBar />
        <DeadEnd
          message={`Type « ${machineName} » introuvable ou définition YAML invalide.`}
          to="/builder/types"
          label="Retour aux types"
        />
      </div>
    )
  }

  if (!isNew && (objectError || !object)) {
    return (
      <div className={shellClass}>
        <PlainBar />
        <DeadEnd
          message={`Contenu « ${id} » introuvable.`}
          to={`/builder/types/${machineName}`}
          label="Retour aux contenus"
        />
      </div>
    )
  }

  const titleForDisplay = primaryField && data[primaryField]
    ? data[primaryField]
    : (object?.label || (isNew ? 'Nouveau contenu' : `#${object?.nid}`))

  const popover = modal && (() => {
    const field = fields.find(f => f.key === modal.key)
    if (!field) return null
    return (
      <FieldEditorPopover
        anchorEl={modal.anchorEl}
        field={field}
        value={data[field.key]}
        onSave={(key, v) => {
          setField(key, v)
          setModal(null)
        }}
        onClose={() => setModal(null)}
      />
    )
  })()

  const surface = {
    type, isNew, machineName, fields, primaryField, data, setField, titleForDisplay,
    slug, setSlug, meta, setMeta, published, setPublished,
    saving, saveError, onSave: handleSave, onDelete: handleDelete,
    models, modelsLoading, activeModel, onSelectModel: selectModel, modelMissing, requestedSlug,
    onOpenModal: (key, e) => setModal({ key, anchorEl: e?.currentTarget ?? null }),
    // Les valeurs d'aperçu du modèle, pour un contenu neuf : seule la surface
    // MONTÉE DANS le modèle voit les définitions de ses blocs.
    onPrefill: applyPreviewPrefill,
    // La surface en a besoin pour retrouver, dans la lunette, le nœud qu'un
    // champ du panneau alimente (cf. le défilement vers le champ actif).
    objectEdit,
  }

  return (
    <div className={shellClass}>
      {activeModel ? (
        // `key` sur le slug : changer de modèle remonte le provider, donc la
        // page chargée, plutôt que de rejouer un chargement dans un état qui
        // décrit encore l'autre.
        <BuilderProvider
          key={activeModel.slug}
          slug={activeModel.slug}
          initialEditMode
          editScope="object"
          objectEdit={objectEdit}
        >
          <ObjectSurfaceInModel {...surface} objectContext={objectContext} />
          {/* Envoi à blanc : en aperçu du modèle, un formulaire n'envoie rien. */}
          <BuilderDryRunPanel />
        </BuilderProvider>
      ) : (
        <ObjectSurface {...surface} view={null} modelsPending={modelsLoading} />
      )}
      {popover}
    </div>
  )
}

/**
 * La surface, MONTÉE DANS le provider du modèle : elle y prend les commandes de
 * vue (prévisualisation, appareil), auxquelles le canevas obéit, et rend le
 * canevas en enfant — donc dans ce même provider.
 */
const ObjectSurfaceInModel = ({ objectContext, ...props }) => {
  const { isEditMode, setEditMode, previewDevice, setPreviewDevice, page, registry } = useBuilder()

  // ─── Pré-remplissage d'un contenu NEUF ────────────────────────────────────
  // C'est ICI, et pas dans l'écran, que les valeurs d'aperçu sont atteignables :
  // elles vivent dans la DÉFINITION des blocs (`rjBuild.data._context_`), que
  // seul le provider du modèle charge (`registry`). Un contenu neuf part donc
  // avec ce que le modèle affiche déjà à l'écran, plutôt qu'avec des champs
  // vides dans une page qui, du coup, ne montre plus rien.
  //
  // Les blocs de premier niveau suffisent : un bloc imbriqué (colonnes, grille)
  // ne rend pas l'ouverture d'un contenu. Le pré-remplissage se joue une fois,
  // à l'arrivée des définitions ; `onPrefill` ignore ensuite tout appel (cf.
  // l'écran), et n'écrase jamais une valeur déjà saisie.
  const { onPrefill, isNew, fields, activeModel } = props
  useEffect(() => {
    if (!onPrefill || !isNew) return
    const blocs = page?.blocs ?? []
    if (blocs.length === 0 || !Array.isArray(registry) || registry.length === 0) return
    const byType = new Map(registry.filter(e => e?.type).map(e => [e.type, e]))
    const defs = blocs.map(b => byType.get(b?.type)).filter(Boolean)
    onPrefill(previewContextValues(defs, { fields, mapping: activeModel?.mapping ?? null }))
  }, [onPrefill, isNew, page, registry, fields, activeModel])

  return (
    <ObjectSurface {...props} view={{ isEditMode, setEditMode, previewDevice, setPreviewDevice }}>
      <BuilderCanvas slug={props.activeModel.slug} objectContext={objectContext} />
    </ObjectSurface>
  )
}

/**
 * La barre, la lunette et le panneau des champs.
 *
 * `view` porte les commandes de vue quand un modèle est monté ; `null` sinon —
 * un type sans modèle n'a rien à prévisualiser, et l'appareil ne décrirait
 * aucune largeur.
 */
const ObjectSurface = ({
  type, isNew, machineName, fields, primaryField, data, setField, titleForDisplay,
  slug, setSlug, meta, setMeta, published, setPublished,
  saving, saveError, onSave, onDelete,
  models, modelsLoading, activeModel, onSelectModel, modelMissing, requestedSlug,
  // `onPrefill` voyage dans le même sac de props mais n'est lu que par la
  // surface montée DANS le modèle (`ObjectSurfaceInModel`, qui seule voit les
  // définitions de blocs) : rien à déstructurer ici.
  onOpenModal, view, children, modelsPending = false, objectEdit,
}) => {
  const navigate = useNavigate()
  const [panelOpen, setPanelOpen] = useState(true)
  // Le champ du panneau sur lequel on est : ce qui s'allume dans la lunette.
  const [activeFieldKey, setActiveFieldKey] = useState(null)
  const [showAddress, setShowAddress] = useState(false)
  const [showMeta, setShowMeta] = useState(false)
  // La barre est `sticky` : elle se cale sous le menu Orbit, dont la hauteur
  // dépend de la disposition choisie (cf. le hook). Sa propre hauteur donne au
  // panneau des champs le point où se coller à son tour.
  const [headerRef, stickyTop, headerHeight] = useOrbitStickyTop()
  // La valeur du titre à la prise de focus : ce qu'Échap rétablit.
  const titleBeforeEdit = useRef('')

  // ÊTRE SUR UN CHAMP, C'EST LE VOIR. Un modèle fait plusieurs écrans de haut ;
  // le champ saisi au panneau peut s'afficher très bas dans la lunette, et son
  // effet passait alors inaperçu. On CALE la page sur l'élément que ce champ
  // alimente et on l'allume (la recette `.is-active` existe déjà, cf.
  // index.css) — sans jamais lui donner le focus : il porte `tabindex="0"`
  // (cf. BuilderBlockWrapper) et la frappe en cours le perdrait.
  //
  // Le canevas ne défile pas chez lui : c'est le DOCUMENT (cf. BuilderShell et
  // BuilderObjectFieldsPanel). D'où `window.scrollTo`, décalé des deux barres
  // `sticky` — un `scrollIntoView` nu ferait atterrir la cible dessous.
  //
  // Le décalage se LIT dans une référence et ne figure pas dans les dépendances :
  // la barre est `flex-wrap` et sa hauteur est mesurée par un `ResizeObserver`
  // (cf. le hook). Un simple passage à la ligne — fenêtre étroite, titre long —
  // rejouerait sinon l'effet, donc un défilement que personne n'a demandé.
  const scrollOffsetRef = useRef(0)
  scrollOffsetRef.current = stickyTop + headerHeight
  useEffect(() => {
    if (!activeFieldKey) return
    // La liaison porte le chemin du MODÈLE ; `resolve` le traduit en champ
    // d'objet — le mapping `_context_` peut renommer, une comparaison de
    // chaînes serait fausse. Si le modèle affiche le champ deux fois, le
    // premier nœud fait l'affaire.
    let target = null
    for (const el of document.querySelectorAll('#builder-canvas [data-builder-context-field]')) {
      if (objectEdit?.resolve?.(el.getAttribute('data-builder-context-field'))?.objectKey === activeFieldKey) {
        target = el
        break
      }
    }
    // Champ que le modèle n'affiche pas : c'est précisément ce que le panneau
    // sert à atteindre — rien à montrer, et rien à signaler.
    if (!target) return
    target.classList.add('is-active')
    const rect = target.getBoundingClientRect()
    const limit = scrollOffsetRef.current + 8
    // Déjà sous les yeux : ne pas remuer la page pour rien.
    if (rect.top < limit || rect.bottom > window.innerHeight - 8) {
      window.scrollTo({
        top: rect.top + window.scrollY - limit - 16,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      })
    }
    return () => target.classList.remove('is-active')
  }, [activeFieldKey, objectEdit])

  // Le titre est le CHAMP PRIMAIRE du type : l'écrire ici l'écrit dans le
  // contenu, donc dans le canevas à la frappe — c'est le même champ que celui
  // du panneau de droite, pas une copie. Un type qui n'en déclare pas, ou dont
  // le champ primaire n'est pas du texte, garde un titre en lecture seule.
  const titleFieldType = fields.find(f => f.key === primaryField)?.type
  const titleEditable = !!primaryField
    && (titleFieldType === 'text' || titleFieldType === undefined)
    && (data[primaryField] == null || typeof data[primaryField] === 'string')
  const backTo = type.siteId ? `/sites/${type.siteId}` : `/builder/types/${machineName}`
  const backLabel = type.siteId ? 'Retour au site' : `Retour aux contenus « ${type.name} »`

  // Combien de balises le contenu renseigne — le bouton le dit sans ouvrir le
  // panneau, comme sur une page.
  const metaCount = meta ? Object.values(meta).filter(v => v !== '' && v != null).length : 0
  // L'adresse publique telle que le MODÈLE la compose : un chemin en `@champ`
  // la dérive des champs saisis (elle change sous la frappe, comme à
  // l'enregistrement), un chemin hérité en `:slug` reprend le segment du contenu.
  const publicPath = previewModelPath(activeModel?.path ?? '', { data, slug })

  return (
    <>
      <header
        id="builder-object-header"
        ref={headerRef}
        className="sticky z-30 bg-white border-b border-gray-200 px-6 py-3 shadow-sm"
        style={{ top: stickyTop }}
      >
        {/* Le titre de l'écran, pour la hiérarchie : à l'œil, c'est le champ
            saisissable ci-dessous qui le porte — mais un `input` n'est pas un
            titre, et la page n'en aurait aucun (RGAA 9.1). */}
        <h1 className="sr-only">Contenu {type.name} — {titleForDisplay}</h1>
        {/* Même structure que la barre d'une page : le groupe de gauche se laisse
            écraser (`min-w-0`) et la barre passe à la ligne plutôt que de laisser
            le titre chevaucher les actions. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate(backTo)}
              className="p-1.5 -ml-1.5 rounded-lg text-gray-400 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              aria-label={backLabel}
              title={backLabel}
            >
              <ArrowLeft size={16} aria-hidden="true" />
            </button>
            {titleEditable ? (
              <input
                type="text"
                value={data[primaryField] ?? ''}
                onChange={(e) => setField(primaryField, e.target.value)}
                // Écriture DIRECTE, contrairement au titre d'une page (qui, lui,
                // ne se commite qu'au blur) : ici le titre EST un champ du
                // contenu, affiché par le modèle — le voir changer sous la frappe
                // est le propre de cette surface, et c'est déjà ce que fait le
                // même champ dans le panneau de droite. Échap rend donc la valeur
                // d'avant la saisie, faute d'un brouillon à jeter.
                onFocus={() => { titleBeforeEdit.current = data[primaryField] ?? '' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() }
                  if (e.key === 'Escape') { setField(primaryField, titleBeforeEdit.current); e.currentTarget.blur() }
                }}
                size={Math.max(String(data[primaryField] ?? '').length + 1, 14)}
                aria-label="Titre du contenu"
                placeholder="Titre du contenu"
                className="min-w-0 max-w-lg px-2 py-1 text-sm font-medium text-gray-800 bg-transparent border border-transparent rounded-md hover:border-gray-200 focus:border-teal-500 focus:bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              />
            ) : (
              <span className="text-gray-700 font-medium text-sm truncate">{titleForDisplay}</span>
            )}
            {/* Le TYPE du contenu, à la place qu'occupe le site sur une page :
                c'est le repère équivalent — ce qu'on est en train d'éditer. Rien
                de plus : le modèle est la lunette, pas un objet de cet écran, et
                un lien vers lui ne menait qu'à quitter l'édition en cours. */}
            <span className="shrink-0 text-xs text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded">
              {type.name}
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <ModelPicker
              models={models}
              loading={modelsLoading}
              value={activeModel?.slug ?? ''}
              onChange={onSelectModel}
            />
            <button
              type="button"
              onClick={() => setShowAddress(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              title={publicPath
                ? `Adresse publique de ce contenu — ${publicPath}`
                : 'Segment d’adresse de ce contenu'}
            >
              <Globe size={14} aria-hidden="true" />
              Adresse
            </button>
            <button
              type="button"
              onClick={() => setShowMeta(true)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
                metaCount > 0
                  ? 'border-teal-300 text-teal-700 bg-teal-50/40 hover:bg-teal-50'
                  : 'border-gray-200 text-gray-600 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50'
              }`}
              title="Métadonnées de référencement et de partage de ce contenu"
            >
              <Tags size={14} aria-hidden="true" />
              Métadonnées{metaCount > 0 ? ` (${metaCount})` : ''}
            </button>
            <label
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-teal-500 ${
                published
                  ? 'border-teal-300 text-teal-700 bg-teal-50/40 hover:bg-teal-50'
                  : 'border-gray-200 text-gray-600 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50'
              }`}
              title={published
                ? 'Contenu publié — décocher le repasse en brouillon, puis Enregistrer'
                : 'Contenu en brouillon (visible des seuls éditeurs) — cocher pour le publier, puis Enregistrer'}
            >
              <input
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                className="w-3.5 h-3.5 accent-teal-700 focus-visible:outline-none"
              />
              Publié
            </label>

            {/* Prévisualisation et appareil décrivent ce que MONTRE la lunette :
                sans modèle monté, il n'y a rien à prévisualiser. */}
            {view && (
              <>
                {view.isEditMode ? (
                  <button
                    type="button"
                    onClick={() => view.setEditMode(false)}
                    className="p-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                    aria-label="Prévisualiser"
                    title="Prévisualiser — le contenu tel que le visiteur le verra"
                  >
                    <Eye size={15} aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => view.setEditMode(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                  >
                    <PenLine size={14} aria-hidden="true" />
                    Modifier
                  </button>
                )}
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5" role="group" aria-label="Aperçu par appareil">
                  {[
                    { id: 'mobile', Icon: Smartphone, label: 'Mobile' },
                    { id: 'tablet', Icon: Tablet, label: 'Tablette' },
                    { id: 'desktop', Icon: Monitor, label: 'Ordinateur' },
                  ].map(d => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => view.setPreviewDevice(d.id)}
                      aria-pressed={view.previewDevice === d.id}
                      className={`p-1.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
                        view.previewDevice === d.id
                          ? 'bg-white shadow-sm text-teal-700'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                      aria-label={d.label}
                      title={`${d.label} (${DEVICE_WIDTHS[d.id]})`}
                    >
                      <d.Icon size={15} aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </>
            )}

            <button
              type="button"
              onClick={() => setPanelOpen(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              aria-expanded={panelOpen}
              aria-controls="object-fields-panel"
            >
              {panelOpen ? <PanelRightClose size={14} aria-hidden="true" /> : <PanelRightOpen size={14} aria-hidden="true" />}
              Champs
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-800 text-white hover:bg-gray-900 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            >
              <Save size={14} aria-hidden="true" />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            {/* Le geste destructeur est le DERNIER, isolé par un filet : on ne le
                rencontre pas en visant Enregistrer (même parti que la barre d'une
                page). */}
            {!isNew && (
              <>
                <span className="mx-0.5 h-5 w-px bg-gray-200" aria-hidden="true" />
                <button
                  type="button"
                  onClick={onDelete}
                  className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-rose-300 hover:text-rose-700 hover:bg-rose-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                  aria-label="Supprimer ce contenu"
                  title="Supprimer ce contenu"
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-stretch min-h-0">
        <div className="flex-1 min-w-0 flex flex-col">
          {modelMissing && (
            <p role="alert" className="alert alert-warning m-4 items-start">
              <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Aucun modèle « {requestedSlug} » ne déclare ce type de contenu.
                {activeModel
                  ? <> C&apos;est le modèle du type qui est ouvert.</>
                  : <> Les champs du contenu s&apos;éditent ci-contre.</>}
              </span>
            </p>
          )}
          {modelsPending ? (
            <div className="flex items-center justify-center py-24" role="status">
              <span className="spinner" aria-hidden="true" />
              <span className="sr-only">Chargement du modèle…</span>
            </div>
          ) : (children ?? <NoModelNotice type={type} />)}
        </div>

        {panelOpen && (
          <BuilderObjectFieldsPanel
            id="object-fields-panel"
            label="Champs du contenu"
            top={stickyTop + headerHeight}
          >
            <div className="p-5 space-y-5">
              <div>
                <h2 className="text-sm font-semibold text-gray-800">Champs du contenu</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Tous les champs de ce contenu, qu&apos;ils soient affichés ou non par ce
                  modèle : c&apos;est ici qu&apos;on atteint ceux qu&apos;il ne montre pas — et qu&apos;on
                  vide ceux dont on ne veut plus.
                </p>
                {/* La règle de la lunette, en TEXTE et non en infobulle : elle
                    explique pourquoi la moitié de ce qu'on voit à gauche ne
                    réagit pas au clic, et une infobulle est perdue au tactile
                    comme au clavier (WCAG 1.4.13). */}
                {activeModel && (
                  <p className="text-xs text-gray-500 mt-2">
                    Le modèle, lui, n&apos;est pas modifiable ici : seuls les éléments liés à un
                    champ de ce contenu s&apos;éditent sur la page — ils y sont cernés d&apos;un
                    fin liseré gris, qui passe au bleu au survol.
                  </p>
                )}
              </div>
              <section className="bg-white border border-gray-200 rounded-xl p-4 space-y-5">
                {fields.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    Aucun champ déclaré dans le schéma.
                  </p>
                )}
                {/* Le `div` ÉCOUTE le focus, il ne le prend pas : React le fait
                    remonter depuis l'input d'un éditeur inline comme depuis le
                    bouton d'une cellule à pop-in — les deux gestes désignent le
                    même champ, sans rien changer à `FieldRow` ni au registre. */}
                {fields.map((field) => (
                  <div key={field.key} onFocus={() => setActiveFieldKey(field.key)}>
                    <FieldRow
                      field={field}
                      value={data[field.key]}
                      onChange={(v) => setField(field.key, v)}
                      // Et au CLIC pour une cellule à pop-in : Safari et
                      // Firefox/macOS ne donnent pas le focus à un `button`
                      // cliqué — le `onFocus` ci-dessus n'y suffirait pas.
                      onOpenModal={(e) => { setActiveFieldKey(field.key); onOpenModal(field.key, e) }}
                    />
                  </div>
                ))}
              </section>
              {saveError && (
                <p role="alert" className="alert alert-danger">{saveError}</p>
              )}
            </div>
          </BuilderObjectFieldsPanel>
        )}
      </div>

      {showAddress && (
        <BuilderObjectAddressPanel
          slug={slug}
          onApply={setSlug}
          onClose={() => setShowAddress(false)}
          modelPath={activeModel?.path ?? ''}
          data={data}
          modelTitle={activeModel?.title ?? ''}
          siteName={type.siteName ?? ''}
          titleFallback={titleForDisplay}
          isNew={isNew}
        />
      )}
      {showMeta && (
        <BuilderMetaPanel
          value={meta}
          onApply={setMeta}
          onClose={() => setShowMeta(false)}
          heading="Métadonnées du contenu"
          titleFallback={titleForDisplay}
          urlPreview={publicPath}
        />
      )}
    </>
  )
}

/** Une bande nue, le temps d'un chargement ou d'une impasse. */
const PlainBar = () => (
  <div className="bg-white border-b border-gray-200 px-6 py-3 shadow-sm h-[3.25rem]" aria-hidden="true" />
)

const DeadEnd = ({ message, to, label }) => (
  <div className="max-w-3xl mx-auto px-6 py-16 text-center">
    <AlertCircle size={28} className="text-amber-500 mx-auto mb-3" aria-hidden="true" />
    <p className="text-sm text-gray-600">{message}</p>
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 mt-4 text-sm text-teal-700 hover:text-teal-900 transition-colors"
    >
      <ChevronLeft size={14} aria-hidden="true" />
      {label}
    </Link>
  </div>
)

/**
 * Un type sans modèle : il n'y a rien à mettre dans la lunette, et ce n'est pas
 * une panne. On dit ce qui manque et où le poser — les champs, eux, s'éditent
 * déjà dans le panneau.
 */
const NoModelNotice = ({ type }) => (
  <div className="max-w-xl mx-auto px-6 py-20 text-center">
    <LayoutTemplate size={28} className="text-gray-300 mx-auto mb-3" aria-hidden="true" />
    <p className="text-sm text-gray-600">
      Ce type de contenu n&apos;a pas encore de modèle : aucune page ne déclare afficher
      ses contenus, il n&apos;y a donc rien à montrer ici.
    </p>
    <Link
      to={`/builder/types/${type.type}/model`}
      className="inline-flex items-center gap-1.5 mt-4 text-sm text-teal-700 hover:text-teal-900 transition-colors"
    >
      <LayoutTemplate size={14} aria-hidden="true" />
      Poser le modèle de « {type.name} »
    </Link>
  </div>
)

/**
 * Le rendu qu'un modèle AFFICHE, quand il n'est pas celui qu'on écrit.
 *
 * Un champ `rich-text` est stocké `{value, format}` et servi avec, en plus, un
 * `processed` calculé par les filtres Drupal du format — c'est cette clé que le
 * gabarit affiche. L'éditeur, lui, ne produit que la source : sans ce miroir,
 * modifier un corps de texte le ferait DISPARAÎTRE à l'écran jusqu'au prochain
 * chargement. On y recopie donc la source, en APPROXIMATION assumée du rendu
 * serveur (les filtres du format ne tournent pas ici) ; le vrai rendu revient au
 * prochain GET, et `processed` est de toute façon restrippé au presave.
 */
const withLocalRender = (fields, key, value) => {
  const field = fields.find(f => f && f.key === key)
  const type = field?.type ?? field?.editorType
  if (type !== 'rich-text') return value
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return value
  return { ...value, processed: value.value ?? '' }
}

/**
 * Le sélecteur de modèle — « dans quoi je regarde ce contenu ».
 *
 * La règle est 1 type = 1 modèle : il n'y a alors rien à choisir, et le
 * sélecteur disparaît. Il ne reste que pour les types HÉRITÉS qui en portent
 * plusieurs — ce que l'onglet « Modèle » du type signale et invite à corriger.
 * Une liste native : ils se comptent sur les doigts d'une main, et le contrôle
 * natif est le plus accessible.
 */
const ModelPicker = ({ models, loading, value, onChange }) => {
  // Le choix ne se pose que pour un type HÉRITÉ qui porte plusieurs modèles :
  // ailleurs il n'y a qu'une surface, et un sélecteur à une entrée n'apprend
  // rien.
  if (loading || models.length < 2) return null
  const options = models
  return (
    <div className="flex items-center gap-1.5">
      <LayoutTemplate size={14} className="text-gray-400" aria-hidden="true" />
      <label htmlFor="object-model-picker" className="sr-only">Modèle d&apos;affichage</label>
      <select
        id="object-model-picker"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
      >
        {options.map(m => (
          <option key={m.slug} value={m.slug}>{m.title}</option>
        ))}
      </select>
    </div>
  )
}
